import React, { useEffect, useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppSettings } from './../../config/app-settings.js';
import { apiFetch } from '../../utils/api';

function MyLogin() {
  const context = useContext(AppSettings);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ▼ 追加：IPアドレスとユーザーエージェントを保存するState
  const [clientIp, setClientIp] = useState('取得中...');
  const [userAgent, setUserAgent] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  // 1. 追い出された時の情報を取得
  let from = location.state?.from?.pathname || '/';

  // 2. もし戻り先が自分自身（ログイン関連）なら、トップページに強制変更
  if (from === '/auth/login' || from === '/login') {
    from = '/';
  }

  // ログイン済みならログイン画面をスキップ
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      // すでにログインしてるなら、トップページ（またはfrom）へ飛ばす
      navigate(from, { replace: true });
    }
  }, [navigate, from]);

  // ▼ 追加：画面表示時にIPアドレスとUser-Agentを取得する処理
  useEffect(() => {
    setUserAgent(navigator.userAgent);

    const fetchIpAddress = async () => {
      try {
        // 外部APIではなく、自分のFastAPI（auth.py）へアクセスする！
        const res = await apiFetch(`/auth/client-ip`);
        // 👆 ここまで変更 👆

        const data = await res.json();
        setClientIp(data.ip);
      } catch (err) {
        setClientIp('取得できませんでした');
      }
    };
    fetchIpAddress();
  }, []);

  // レイアウト設定の調整
  useEffect(() => {
    context.handleSetAppSidebarNone(true);
    context.handleSetAppHeaderNone(true);
    context.handleSetAppContentClass('p-0');

    return () => {
      context.handleSetAppSidebarNone(false);
      context.handleSetAppHeaderNone(false);
      context.handleSetAppContentClass('');
    };
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      // FastAPIのOAuth2PasswordRequestFormに合わせ、x-www-form-urlencoded形式のボディを作成
      const formData = new FormData();
      // FastAPIのルールに合わせて、キー名は絶対に 'username' にする！
      formData.append('username', email);
      formData.append('password', password);

      // fetch APIによるPOSTリクエストの実装
      const response = await apiFetch(`/auth/login?remember_me=${rememberMe}`, {
        method: 'POST',
        //body: params
        body: formData,
      });

      // エラーハンドリングの厳格化
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorException = new Error(errorData.detail || 'ログインに失敗しました');
        errorException.status = response.status;
        errorException.detail = errorData.detail;
        throw errorException;
      }

      // レスポンスからJSONデータ（アクセストークンなど）を解析して抽出
      const data = await response.json();

      // 成功時：トークンとユーザー名を保存
      localStorage.setItem('access_token', data.access_token);
      await context.loadUserProfile();

      // 'from' に指定された場所へ飛ばす
      navigate(from, { replace: true });

    } catch (err) {
      if (err && (err.status === 400 || err.status === 401)) {
        setError(err.detail || 'メールアドレスまたはパスワードが違います');
      } else {
        // 完全にサーバーと通信ができなかった場合（ネットワーク切断等）
        setError('サーバーに接続できませんでした。しばらくしてから再試行してください。');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login login-with-news-feed">
      <div className="news-feed">
        <div className="news-image" style={{ backgroundImage: 'url(/assets/img/login-bg/IMG_3579.jpg)' }}></div>
        {/* ▼ 変更：テキストをTwinkleGirlsとIP/UA表示に差し替え */}
        <div className="news-caption">
          <h4 className="caption-title"><img src="/assets/img/logo/icon.png" alt="TwinkleGirls" style={{ width: '48px', height: '48px' }} />
            <b className="ms-2">TwinkleGirls</b>
          </h4>
          <p style={{ wordBreak: 'break-all', fontSize: '16px', opacity: 0.8 }}>
            IPaddress: {clientIp}<br />
            HTTP_USER_AGENT: {userAgent}
          </p>
        </div>
      </div>
      <div className="login-container">
        <div className="login-header mb-30px">
          <div className="brand">
            <div className="d-flex align-items-center">
              <span
                className="siteLogo d-inline-block me-2"
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundImage: 'url(/assets/img/logo/icon.png)',
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  verticalAlign: 'middle'
                }}
              ></span>
              <b>Twinkle</b> Girls
            </div>
            <small>Admin Control Panel</small>
          </div>
          <div className="icon">
            <i className="fa fa-sign-in-alt"></i>
          </div>
        </div>
        <div className="login-content">
          <form onSubmit={handleSubmit} className="fs-13px">
            {error && (
              <div className="alert alert-danger mb-15px">
                {error}
              </div>
            )}
            <div className="form-floating mb-15px">
              <input
                type="text"
                className="form-control h-45px fs-13px"
                placeholder="Email Address"
                id="emailAddress"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <label htmlFor="emailAddress" className="d-flex align-items-center fs-13px text-gray-600">Email Address</label>
            </div>
            <div className="form-floating mb-15px">
              <input
                type="password"
                className="form-control h-45px fs-13px"
                placeholder="Password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <label htmlFor="password" className="d-flex align-items-center fs-13px text-gray-600">Password</label>
            </div>
            <div className="form-check mb-30px">
              <input
                className="form-check-input"
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="rememberMe">
                Remember Me
              </label>
            </div>
            <div className="mb-15px">
              <button type="submit" className="btn btn-theme d-block h-45px w-100 btn-lg fs-14px" disabled={loading}>
                {loading ? 'ログイン中...' : 'Sign me in'}
              </button>
            </div>
            <hr className="bg-gray-600 opacity-2" />
            <div className="text-gray-600 text-center text-gray-500-darker mb-0">
              &copy; TwinkleGirls All Right Reserved
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default MyLogin;